import { Box, Skeleton, useMantineTheme } from '@mantine/core';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

import { logError } from '~/common/utils/log';
import dt from '~/testing';

import { resolveWhopIconUrl } from './resolveWhopIconUrl';

export const CRYPTO_QR_SIZE = 192;
const ASSET_ICON_OVERLAY_SIZE = 40;

type CryptoQrCodeProps = {
  /** The deposit address. Rendered into a canvas locally; never sent anywhere. */
  value: string;
  alt: string;
  size?: number;
  /** Optional asset icon rendered centred over the QR code (decorative — alt is always ""). */
  assetIconUrl?: string | null;
};

export function CryptoQrCode({
  value,
  alt,
  size = CRYPTO_QR_SIZE,
  assetIconUrl,
}: CryptoQrCodeProps) {
  const theme = useMantineTheme();
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [hasFailed, setHasFailed] = useState(false);
  // EDGE-32160: the asset-icon URL comes from Whop's catalog and can 404 at runtime (all currently do
  // — EDGE-32159). Without this, the browser paints its broken-image glyph over the QR centre. On
  // error we drop the overlay entirely (plain QR, no white knockout) rather than show a broken badge.
  const [hasIconFailed, setHasIconFailed] = useState(false);

  // Re-arm the overlay if the icon URL changes (a different asset may have a working icon).
  useEffect(() => {
    setHasIconFailed(false);
  }, [assetIconUrl]);

  useEffect(() => {
    let isCancelled = false;
    setDataUrl(null);
    setHasFailed(false);

    // eslint-disable-next-line local-rules/use-theme-tokens -- `margin` here is qrcode's quiet-zone module count, not a CSS length
    QRCode.toDataURL(value, { width: size, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!isCancelled) setDataUrl(url);
      })
      .catch((error: unknown) => {
        if (isCancelled) return;
        setHasFailed(true);
        // Log the error class only — never the input value.
        logError(
          'Crypto deposit QR generation failed',
          error instanceof Error ? error.name : 'UnknownError'
        );
      });

    return () => {
      isCancelled = true;
    };
  }, [value, size]);

  if (hasFailed) return null;
  if (!dataUrl) {
    return (
      <Skeleton
        width={size}
        height={size}
        radius={theme.radius.md}
        data-test-id={dt.payments.deposits.cryptoDirect.qrLoading}
      />
    );
  }

  return (
    <Box sx={{ position: 'relative', width: size, height: size, lineHeight: 0 }}>
      {/* eslint-disable-next-line @next/next/no-img-element -- data URL generated on the client; nothing to optimize */}
      <img
        src={dataUrl}
        alt={alt}
        width={size}
        height={size}
        style={{ borderRadius: theme.radius.md, display: 'block' }}
        data-test-id={dt.payments.deposits.cryptoDirect.qrCode}
      />
      {assetIconUrl && !hasIconFailed && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- decorative overlay; alt is always "" */}
          <img
            src={resolveWhopIconUrl(assetIconUrl)}
            alt=""
            width={ASSET_ICON_OVERLAY_SIZE}
            height={ASSET_ICON_OVERLAY_SIZE}
            onError={() => setHasIconFailed(true)}
            style={{
              display: 'block',
              borderRadius: '50%',
              border: `3px solid ${theme.white}`,
              background: theme.white,
            }}
          />
        </Box>
      )}
    </Box>
  );
}
